'use client'

/**
 * ReplyBehaviorSettings — the §4/§8 admin/agent control for how the agent handles
 * inbound replies (the P5 Settings placeholder, now real).
 *
 * One calm setting, read as plain-language consequences: **escalation rules** —
 * per reply class, auto-handle vs always route to a human. The §8 "never
 * auto-send" classes are LOCKED to a human (shown, but not editable) so a risky
 * reply can never be auto-handled. The agent only classifies and flags — you
 * write every reply yourself.
 *
 * Admin-scoped: only `outreach.admin` may change these (the server enforces it;
 * the UI renders read-only otherwise). Changes are audited server-side.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  Lock,
  Check,
  Info,
  Loader2,
  AlertCircle,
  ShieldCheck,
  UserRound,
} from 'lucide-react'
import { Button, Badge, Card } from '@/components/ui-v2'
import { useAuth } from '@/auth/AuthProvider'
import { settingsApi } from '../services/outreach.api'
import { REPLY_CLASS_META } from '../constants'
import type {
  EscalationRule,
  ReplyBehaviorView,
  ReplyBehaviorClassRule,
  ReplyClass,
  UpdateReplyBehaviorPayload,
} from '../types'

function errMsg(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string }; status?: number }; message?: string }
  return e?.response?.data?.error || e?.message || fallback
}

/** Map a per-class rule into its plain-language consequence. */
function consequence(rule: ReplyBehaviorClassRule, value: EscalationRule): string {
  if (rule.locked) return 'Always waits for you'
  if (value === 'human') return 'Waits for you'
  if (rule.draftable) return 'Flagged — you reply'
  return 'Auto-handled'
}

export function ReplyBehaviorSettings() {
  const { hasPermission } = useAuth()
  const canEdit = hasPermission('outreach.admin')

  const [view, setView] = useState<ReplyBehaviorView | null>(null)
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading')
  const [loadError, setLoadError] = useState<string | null>(null)

  // Local draft (admin edits these before saving).
  const [rules, setRules] = useState<Record<string, EscalationRule>>({})

  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const hydrate = (v: ReplyBehaviorView) => {
    setView(v)
    setRules(Object.fromEntries(v.rules.map((r) => [r.class, r.rule])))
  }

  const load = async () => {
    setStatus('loading')
    setLoadError(null)
    try {
      const { data } = await settingsApi.getReplyBehavior()
      hydrate(data.replyBehavior)
      setStatus('loaded')
    } catch (err) {
      setStatus('error')
      setLoadError(errMsg(err, 'Failed to load reply behavior'))
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dirty = useMemo(() => {
    if (!view) return false
    return view.rules.some((r) => rules[r.class] !== r.rule)
  }, [view, rules])

  const setRule = (cls: string, value: EscalationRule) => {
    setRules((prev) => ({ ...prev, [cls]: value }))
    setSaved(false)
  }

  const handleSave = async () => {
    if (!view) return
    setSaving(true)
    setSaveError(null)
    setSaved(false)
    try {
      // Send only the configurable classes (server ignores/locks the rest).
      const escalationRules: Partial<Record<ReplyClass, EscalationRule>> = {}
      for (const r of view.rules) {
        if (!r.locked) escalationRules[r.class] = rules[r.class]
      }
      const payload: UpdateReplyBehaviorPayload = { escalationRules }
      const { data } = await settingsApi.updateReplyBehavior(payload)
      hydrate(data.replyBehavior)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (err) {
      setSaveError(errMsg(err, 'Failed to save reply behavior'))
    } finally {
      setSaving(false)
    }
  }

  if (status === 'loading') {
    return (
      <div className="flex items-center gap-2 py-16 justify-center" style={{ color: 'var(--text-secondary)' }}>
        <Loader2 size={16} className="animate-spin" style={{ color: 'var(--accent)' }} />
        <span className="text-[13px]">Loading reply behavior…</span>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div
        className="flex items-center justify-between gap-3 p-3 rounded-lg"
        style={{ background: 'var(--bg-danger)', color: 'var(--fg-danger)' }}
      >
        <span className="flex items-center gap-2 text-[13px]">
          <AlertCircle size={15} strokeWidth={2} />
          {loadError}
        </span>
        <button onClick={load} className="text-[12px] font-medium underline">
          Retry
        </button>
      </div>
    )
  }

  if (!view) return null

  const configurable = view.rules.filter((r) => !r.locked)
  const locked = view.rules.filter((r) => r.locked)

  return (
    <div className="flex flex-col gap-6">
      {/* Read-only notice for non-admins */}
      {!canEdit && (
        <div
          className="flex items-start gap-2 p-2.5 rounded-lg"
          style={{ background: 'var(--bg-info)', color: 'var(--fg-info)' }}
        >
          <Info size={14} strokeWidth={2} className="mt-0.5 shrink-0" />
          <p className="text-[12px] leading-relaxed">
            Only workspace admins can change reply behavior. You can see the current settings here.
          </p>
        </div>
      )}

      {/* ─── Escalation rules ─── */}
      <section className="flex flex-col gap-3">
        <div>
          <h3 className="text-[15px] font-medium" style={{ color: 'var(--text-primary)' }}>
            What waits for a human
          </h3>
          <p className="text-[13px] mt-0.5" style={{ color: 'var(--text-secondary)' }}>
            The agent classifies and flags replies in your inbox — you write every reply. Warm, sensitive, or
            unclear replies always wait for you.
          </p>
        </div>

        <Card padding="lg" className="flex flex-col">
          {configurable.map((r, i) => (
            <RuleRow
              key={r.class}
              rule={r}
              value={rules[r.class] || r.rule}
              disabled={!canEdit}
              divider={i > 0}
              onChange={(v) => setRule(r.class, v)}
            />
          ))}
        </Card>

        {/* Locked / always-human classes (§8 never auto-send) */}
        <div>
          <div className="flex items-center gap-1.5 mb-2 mt-1">
            <Lock size={12} strokeWidth={2} style={{ color: 'var(--text-tertiary)' }} />
            <span className="text-[12px] font-medium" style={{ color: 'var(--text-tertiary)' }}>
              Always handled by a person
            </span>
          </div>
          <Card padding="lg" className="flex flex-col">
            {locked.map((r, i) => (
              <RuleRow
                key={r.class}
                rule={r}
                value="human"
                disabled
                divider={i > 0}
                onChange={() => {}}
              />
            ))}
          </Card>
          <p className="text-[12px] mt-2 leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
            Opt-outs, compensation, visa, remote, wrong-person, unclear, and sensitive replies always go to a person —
            the agent never drafts or sends these.
          </p>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <div
        className="flex items-center justify-between gap-3 pt-1"
        style={{ borderTop: '1px solid var(--border-muted)' }}
      >
        <div className="text-[12px] pt-3" style={{ color: 'var(--text-tertiary)' }}>
          {view.updatedBy && view.updatedAt
            ? `Last changed by ${view.updatedBy.name} · ${new Date(view.updatedAt).toLocaleDateString()}`
            : 'Using the recommended defaults.'}
        </div>
        <div className="flex items-center gap-2 pt-3">
          {saveError && (
            <span className="flex items-center gap-1 text-[12px]" style={{ color: 'var(--fg-danger)' }}>
              <AlertCircle size={13} strokeWidth={2} /> {saveError}
            </span>
          )}
          {saved && (
            <span className="flex items-center gap-1 text-[12px]" style={{ color: 'var(--fg-success)' }}>
              <Check size={13} strokeWidth={2.5} /> Saved
            </span>
          )}
          <Button variant="primary" size="md" disabled={!canEdit || !dirty || saving} onClick={handleSave}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  )
}

/* ─── One per-class rule row ─── */

function RuleRow({
  rule,
  value,
  disabled,
  divider,
  onChange,
}: {
  rule: ReplyBehaviorClassRule
  value: EscalationRule
  disabled?: boolean
  divider?: boolean
  onChange: (v: EscalationRule) => void
}) {
  const meta = REPLY_CLASS_META[rule.class]
  return (
    <div
      className="flex items-center justify-between gap-3 py-2.5"
      style={divider ? { borderTop: '1px solid var(--border-muted)' } : undefined}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium" style={{ color: 'var(--text-primary)' }}>
            {meta?.label || rule.class}
          </span>
          <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
            {consequence(rule, value)}
          </span>
        </div>
        <p className="text-[12px] mt-0.5 truncate" style={{ color: 'var(--text-secondary)' }}>
          {rule.description}
        </p>
      </div>

      {rule.locked ? (
        <Badge variant="default">
          <Lock size={11} strokeWidth={2} /> Human
        </Badge>
      ) : (
        <Segmented value={value} disabled={disabled} onChange={onChange} />
      )}
    </div>
  )
}

/* ─── Auto-handle / Human segmented control ─── */

function Segmented({
  value,
  disabled,
  onChange,
}: {
  value: EscalationRule
  disabled?: boolean
  onChange: (v: EscalationRule) => void
}) {
  const options: { id: EscalationRule; label: string; Icon: typeof ShieldCheck }[] = [
    { id: 'auto_handle', label: 'Agent', Icon: ShieldCheck },
    { id: 'human', label: 'Me', Icon: UserRound },
  ]
  return (
    <div
      className="flex items-center rounded-lg p-0.5 shrink-0"
      style={{ background: 'var(--surface-tertiary)', opacity: disabled ? 0.6 : 1 }}
    >
      {options.map(({ id, label, Icon }) => {
        const active = value === id
        return (
          <button
            key={id}
            onClick={() => !disabled && onChange(id)}
            disabled={disabled}
            className="flex items-center gap-1 h-6 px-2 rounded-md text-[12px] font-medium transition-colors"
            style={{
              background: active ? 'var(--surface-primary)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-tertiary)',
              boxShadow: active ? 'var(--shadow-sm)' : 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            <Icon size={12} strokeWidth={1.75} />
            {label}
          </button>
        )
      })}
    </div>
  )
}

ReplyBehaviorSettings.displayName = 'ReplyBehaviorSettings'
